/**
 * Zoom Translation Utilities
 *
 * Translates between our domain model (100% = pixel-perfect native size)
 * and CSS transform scale (relative to object-contain size).
 *
 * Our Model:
 * - 100% zoom = display at native size (1 source pixel = 1 physical pixel on DPR displays)
 * - Zoom levels are percentages of native image size (50%, 100%, 200%)
 *
 * CSS Transform Model:
 * - scale 1.0 = object-contain size (fits in container)
 * - scale 2.0 = 2x the object-contain size
 *
 * @module zoomTranslation
 */

import type { ZoomLevel } from '@/hooks/useZoomPan';
import logger from '@/utils/logger';

/**
 * Calculate initial scale factor when image loads with object-contain.
 * This tells us how much object-contain scaled the image to fit container.
 *
 * @param img - The loaded image element
 * @returns Scale factor (e.g., 0.25 if 4000px image fit in 1000px container)
 */
export function calculateInitialScaleFactor(img: HTMLImageElement): number {
  if (!img.naturalWidth || !img.naturalHeight) {
    logger.warn('ZoomTranslation', 'Image not loaded, returning default scale factor', { default: 1.0 });
    return 1.0;
  }

  const scaleX = img.clientWidth / img.naturalWidth;
  const scaleY = img.clientHeight / img.naturalHeight;

  // object-contain uses the smaller ratio to fit entire image
  return Math.min(scaleX, scaleY);
}

/**
 * Translate from our zoom model to TransformWrapper scale.
 *
 * Formula: scale = (ourZoom / 100) / (dpr * initialScaleFactor)
 *
 * Example on 2x DPR display:
 * - Image: 4000×3000px native
 * - Container: 1000px wide
 * - object-contain scales to 1000×750 CSS pixels (initialScaleFactor = 0.25)
 * - Our 100% zoom = 2000×1500 CSS pixels (4000 / 2 DPR = pixel-perfect)
 * - Transform scale needed = 2000 / 1000 = 2.0
 * - Calculation: (100 / 100) / (2 * 0.25) = 2.0 ✓
 *
 * @param ourZoomPercent - Our zoom level (50, 100, 200, etc.) or 'fit'
 * @param dpr - Device pixel ratio (window.devicePixelRatio)
 * @param initialScaleFactor - How much object-contain scaled the image
 * @returns Transform scale to apply (1.0 = object-contain size)
 */
export function translateOurZoomToTransformScale(
  ourZoomPercent: ZoomLevel,
  dpr: number,
  initialScaleFactor: number
): number {
  if (ourZoomPercent === 'fit') {
    return 1.0; // object-contain size
  }

  if (initialScaleFactor === 0) {
    logger.warn('ZoomTranslation', 'Invalid scale factor, returning default', { initialScaleFactor, default: 1.0 });
    return 1.0;
  }

  return (ourZoomPercent / 100) / (dpr * initialScaleFactor);
}

/**
 * Translate from TransformWrapper scale to our zoom model.
 * Used when user pinch-zooms with trackpad - we translate back to our percentage.
 *
 * @param transformScale - Current TransformWrapper scale
 * @param dpr - Device pixel ratio
 * @param initialScaleFactor - How much object-contain scaled the image
 * @returns Our zoom percentage (e.g., 137.5)
 */
export function translateTransformScaleToOurZoom(
  transformScale: number,
  dpr: number,
  initialScaleFactor: number
): number {
  // Reverse the formula: ourZoom = transformScale * dpr * initialScaleFactor * 100
  return transformScale * dpr * initialScaleFactor * 100;
}

/**
 * Calculate exact target CSS dimensions for our zoom level.
 * This is what we need for pixel-perfect rendering.
 *
 * At 100% zoom on 2x DPR display:
 * - 4000px source → 2000 CSS pixels
 * - 2000 CSS pixels × 2 DPR = 4000 physical pixels
 * - Perfect 1:1 mapping!
 *
 * @param nativeWidth - Image natural width in pixels
 * @param nativeHeight - Image natural height in pixels
 * @param ourZoomPercent - Our zoom level (50, 100, 200, etc.)
 * @param dpr - Device pixel ratio
 * @returns Target CSS dimensions for pixel-perfect rendering
 */
export function calculateTargetDimensions(
  nativeWidth: number,
  nativeHeight: number,
  ourZoomPercent: number,
  dpr: number
): { width: number; height: number } {
  const targetCssWidth = (nativeWidth * ourZoomPercent / 100) / dpr;
  const targetCssHeight = (nativeHeight * ourZoomPercent / 100) / dpr;

  return { width: targetCssWidth, height: targetCssHeight };
}

/**
 * Find nearest zoom level from our preset list.
 * Used to snap free zoom (from trackpad) to nearest preset.
 *
 * @param ourZoom - Arbitrary zoom percentage (e.g., 137.5)
 * @param presets - Available zoom levels
 * @returns Nearest preset zoom level
 */
export function findNearestZoomLevel(
  ourZoom: number,
  presets: readonly ZoomLevel[]
): ZoomLevel {
  // Filter out 'fit' for numeric comparison
  const numericPresets = presets.filter((z): z is Exclude<ZoomLevel, 'fit'> => typeof z === 'number');

  if (numericPresets.length === 0) {
    return 'fit';
  }

  // Find closest preset
  let nearest: Exclude<ZoomLevel, 'fit'> = numericPresets[0];
  let minDiff = Math.abs(ourZoom - nearest);

  for (const preset of numericPresets) {
    const diff = Math.abs(ourZoom - preset);
    if (diff < minDiff) {
      minDiff = diff;
      nearest = preset;
    }
  }

  return nearest;
}
